import { z } from 'zod';
import { TransactionSchema } from '@/schemas/models/transaction';
import { UserMinimalSchema, UserPhotoSchema } from '@/schemas/models/user';
import { ListingMinimalSchema, ListingPhotoSchema } from '@/schemas/models/listing';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { dateValidator, nonNegativeIntValidator } from '@/schemas/common/validators';

/**
 * GET /api/transactions/seller
 * Fetches all transactions where the authenticated user is the seller
 */

/**
 * Address schema for shipping
 */
export const TransactionAddressSchema = z.object({
  id: nonNegativeIntValidator,
  /** '' when the address carries no contact name — ADDRESS.fullName is nullable. */
  fullName: z.string(),
  line1: z.string(),
  line2: z.string().nullable(),
  locality: z.string(),
  region: z.string(),
  zipCode: z.string(),
  countryId: nonNegativeIntValidator,
});

export type TransactionAddress = z.infer<typeof TransactionAddressSchema>;

/**
 * Shipping schema for transaction
 */
export const ShippingSchema = z.object({
  id: nonNegativeIntValidator,
  status: z.number(),
  shippingProviderId: z.number().nullable(),
  trackingNumber: z.string().nullable(),
  labelUrl: z.string().nullable(),
  externalId: z.string().nullable(),
  /**
   * Shippo service level (usps_priority, ...): a USPS label offers "Use QR-Code
   * Instead" (utils/usps-qr.ts). Optional so a client a deploy ahead still parses.
   */
  serviceToken: z.string().nullable().optional(),
  estimateAmount: z.string().nullable(),
  amount: z.string().nullable(),
  toAddress: TransactionAddressSchema.nullable(),
});

/**
 * Coupon redemption info attached to a seller transaction
 */
export const CouponRedemptionInfoSchema = z.object({
  id: nonNegativeIntValidator,
  couponId: nonNegativeIntValidator,
  couponName: z.string(),
  couponCode: z.string().nullable(),
  couponType: z.enum(['buyer', 'seller']),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.number(),
  discountAmount: z.number(),
  applicationMode: z.enum(['auto', 'code']),
  isPlatformCoupon: z.boolean(),
});

export type CouponRedemptionInfo = z.infer<typeof CouponRedemptionInfoSchema>;

/**
 * Seller transaction schema
 * Extends base transaction with buyer details and minimal listing info
 */
export const SellerTransactionSchema = TransactionSchema.extend({
  /** Buyer information */
  buyer: UserMinimalSchema.extend({
    /**
     * Buyer email address, or '' when the account has none.
     *
     * USERS.email is nullable and roughly a quarter of new signups are
     * phone-only, so this cannot be `z.string().email()`: the route coalesces
     * null to '', that failed the format check, validateResponse threw, and the
     * seller lost their entire Sales list to a 500 over one row.
     */
    email: z.string(),
    /** Buyer trust level */
    trustLevel: nonNegativeIntValidator.optional(),
    /** Buyer profile photos */
    photos: UserPhotoSchema.optional(),
  }).nullable(),
  /** Associated listing details */
  listing: ListingMinimalSchema.extend({
    /** Listing photos */
    photos: z.array(ListingPhotoSchema).optional(),
    /** 1 when the item ships. The sale page shows "Arranged Separately" for a shippable sale with no shipping amount. */
    shippable: z.number().int().optional(),
    /** Category id. The sale page prints its label under the title. */
    categoryId: z.number().int().optional(),
    /** Where the listing came from. The thumbnail needs it to find a feed listing's photo. */
    dataSourceId: z.number().int().nullable().optional(),
    /** When the item was listed, falling back to the sale date. */
    createdDate: dateValidator.optional(),
  }).optional(),
  /** Shipping information with buyer address */
  shipping: ShippingSchema.nullable().optional(),
  /** Buyer's shipping address (fallback when no shipping record exists) */
  buyerAddress: TransactionAddressSchema.nullable().optional(),
  /** Coupon redemption details if a coupon was applied */
  couponRedemption: CouponRedemptionInfoSchema.nullable().optional(),
  /**
   * What Stripe withheld for Sellwild on this sale, in dollars, so the sale
   * page can show the transfer: `price - applicationFeeAmount`. Null on sales
   * from before 2026-09-25. Optional so a response from an older deploy still
   * parses in a newer client.
   */
  applicationFeeAmount: z.number().min(0).nullable().optional(),
});

export type SellerTransaction = z.infer<typeof SellerTransactionSchema>;

/**
 * Success response schema for seller transactions
 * Returns array of transactions where user is the seller
 */
/**
 * One entry per status a sale can be in, so a filter rail can show counts.
 * Keys are the canonical transaction statuses in data/js/transaction-statuses.
 */
export const TransactionStatusCountsSchema = z.object({
  all: z.number(),
  authorized: z.number(),
  accepted: z.number(),
  delivering: z.number(),
  delivered: z.number(),
  completed: z.number(),
  disputed: z.number(),
  cancelled: z.number(),
});

export type TransactionStatusCounts = z.infer<typeof TransactionStatusCountsSchema>;

/**
 * Query parameters for GET /api/transactions/seller.
 *
 * Paging is OPT-IN: without `page` the endpoint returns every transaction, as it
 * always has, because AppSessionProvider loads the whole set on sign-in and
 * counts unread sales from it. Passing `page` switches it to a bounded window
 * and adds the totals — which also bounds the per-transaction lookups this
 * endpoint does in a loop.
 */
export const GetSellerTransactionsQuerySchema = z.object({
  listingId: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  status: z.coerce.number().int().min(0).max(7).optional(),
  q: z.string().trim().max(200).optional(),
});

export type GetSellerTransactionsQuery = z.infer<typeof GetSellerTransactionsQuerySchema>;

export const GetSellerTransactionsSuccessSchema = z.object({
  success: z.literal(true),
  /** Array of seller transactions */
  transactions: z.array(SellerTransactionSchema),
  /** Present only for a paged request; absent keeps the legacy shape intact. */
  total: z.number().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
  hasMore: z.boolean().optional(),
  counts: TransactionStatusCountsSchema.optional(),
});

export type GetSellerTransactionsSuccess = z.infer<typeof GetSellerTransactionsSuccessSchema>;

/**
 * Complete response schema for seller transactions
 * Discriminated union of success and error responses
 */
export const GetSellerTransactionsResponseSchema = z.discriminatedUnion('success', [
  GetSellerTransactionsSuccessSchema,
  ErrorResponseSchema,
]);

export type GetSellerTransactionsResponse = z.infer<typeof GetSellerTransactionsResponseSchema>;
